import { IApiBaseProps, IErrorResponse } from '@common/api.models';
import { isValidApiBaseProps } from '@common/api.services';
import { FETCH_QUERIES_NAME, MUTATION_KEYS } from '@common/constants';
import { createUseFetchSearchFn } from '@common/search.services';
import { isPositiveNumber } from '@core/Primitives/number.services';
import { IItemResponseBase } from '@gcl/react-core';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { AxiosErrorResponse } from '@core/error.models';
import { IRevision } from '@revision/revision.models';
import { IDesignBriefSearch, IDesignBriefSearchFilter } from './designBrief.models';
import {
  getDesignBriefAsync,
  createDesignBriefAsync,
  searchDesignBriefAsync,
  updateDesignBriefAsync,
  createAndIssueDesignBriefToTwcAsync,
  updateAndIssueDesignBriefToTwcAsync,
  updateAndIssueDesignBriefToTwdAsync,
  createDesignBriefRevisionAsync,
  getDesignBriefRevisionsAsync,
} from './designBrief.services';
import {
  ICreateDesignBriefRequest,
  ICreateDesignBriefRevisionRequest,
  IDesignBrief,
  IUpdateDesignBriefRequest,
} from './detail/designBriefDetailPage.models';

export const useFetchDesignBriefSearch = createUseFetchSearchFn<IDesignBriefSearchFilter, IDesignBriefSearch>({
  queryName: FETCH_QUERIES_NAME.DESIGN_BRIEF_SEARCH,
  searchAsyncFn: searchDesignBriefAsync,
});

export const useFetchDesignBrief = (props: IApiBaseProps, id?: number, enabled = true) => {
  return useQuery<IDesignBrief | undefined, AxiosErrorResponse>(
    [props.projectId, id, FETCH_QUERIES_NAME.DESIGN_BRIEF_DETAIL],
    async () => {
      const results = await getDesignBriefAsync(id as number, props);
      return results.item;
    },
    {
      enabled: !!id && isValidApiBaseProps(props) && enabled,
    },
  );
};

export const useCreateDesignBrief = () => {
  return useMutation<
    IItemResponseBase<IDesignBrief>,
    AxiosError<IErrorResponse, any>,
    ICreateDesignBriefRequest,
    unknown
  >([MUTATION_KEYS.CREATE_DESIGN_BRIEF], async (request: ICreateDesignBriefRequest) => {
    const result = await createDesignBriefAsync(request);
    return result;
  });
};

export const useCreateAndIssueDesignBriefToTwc = () => {
  return useMutation<
    IItemResponseBase<IDesignBrief>,
    AxiosError<IErrorResponse, any>,
    ICreateDesignBriefRequest,
    unknown
  >([MUTATION_KEYS.CREATE_AND_ISSUE_DESIGN_BRIEF], async (request: ICreateDesignBriefRequest) => {
    const result = await createAndIssueDesignBriefToTwcAsync(request);
    return result;
  });
};

export const useGetDesignBriefRevisions = (props: IApiBaseProps, id?: number, enabled = true) => {
  return useQuery<IRevision[] | undefined, AxiosErrorResponse>(
    [props.projectId, id, FETCH_QUERIES_NAME.LOADING_REMOVAL_CERT_DETAIL],
    async () => {
      if (!id) {
        return undefined;
      }
      const results = await getDesignBriefRevisionsAsync(id, { id, projectId: props.projectId });
      return results.items;
    },
    {
      enabled: isPositiveNumber(id) && isValidApiBaseProps(props) && enabled,
    },
  );
};

export const useCreateDesignBriefRevision = () => {
  return useMutation<
    IItemResponseBase<IDesignBrief>,
    AxiosError<IErrorResponse, any>,
    ICreateDesignBriefRevisionRequest,
    unknown
  >([MUTATION_KEYS.CREATE_DESIGN_BRIEF], async (request: ICreateDesignBriefRevisionRequest) => {
    const result = await createDesignBriefRevisionAsync(request.id, request);
    return result;
  });
};

export const useUpdateAndIssueDesignBriefToTwc = () => {
  return useMutation<
    IItemResponseBase<IDesignBrief>,
    AxiosError<IErrorResponse, any>,
    IUpdateDesignBriefRequest,
    unknown
  >([MUTATION_KEYS.UPDATE_DESIGN_BRIEF], async (request: IUpdateDesignBriefRequest) => {
    const result = await updateAndIssueDesignBriefToTwcAsync(request.id, request);
    return result;
  });
};

export const useUpdateAndIssueDesignBriefToTwd = () => {
  return useMutation<
    IItemResponseBase<IDesignBrief>,
    AxiosError<IErrorResponse, any>,
    IUpdateDesignBriefRequest,
    unknown
  >([MUTATION_KEYS.UPDATE_DESIGN_BRIEF], async (request: IUpdateDesignBriefRequest) => {
    const result = await updateAndIssueDesignBriefToTwdAsync(request.id, request);
    return result;
  });
};

export const useUpdateDesignBrief = () => {
  return useMutation<
    IItemResponseBase<IDesignBrief>,
    AxiosError<IErrorResponse, any>,
    IUpdateDesignBriefRequest,
    unknown
  >([MUTATION_KEYS.UPDATE_DESIGN_BRIEF], async (request: IUpdateDesignBriefRequest) => {
    const result = await updateDesignBriefAsync(request.id, request);
    return result;
  });
};
